import { Server } from "@wajeez/wajeez.microservices";
import type { Language } from "../localizations/i18n";
import type { Theme } from "../theme/DarkModeContext";
import type { B2BContextData } from "@modules/content/B2BContext";

export type PreferencesData = {
  theme: Theme;
};

export type TokenData = {
  expiry: number;
  token: string;
  refreshToken: string;
};

export type UserData = {
  onboardingCompleted: boolean;
};

export type LanguageData = {
  language: Language;
};

export type Player = {
  isShown: boolean;
};

export type LatestPlayingContent = {
  pageId: string;
  content: Server.ContentEntityOutput;
  item: { [key: string]: string | string[] | null | undefined };
  voiceOvers?: {
    data: Server.ContentEntityVoiceOverOutput[];
    currentVoiceOver: Server.ContentEntityVoiceOverOutput | undefined;
  };
};

type StorageDataMap = {
  TOKEN: TokenData;
  USER: UserData;
  PREFERENCES: PreferencesData;
  LANGUAGE: LanguageData;
  B2B: B2BContextData;
  PLAYER: Player;
  INIT_URL: string;
  LATEST_PLAYING_CONTENT: LatestPlayingContent;
  AUTO_AUTHENTICATED: boolean;
};

export type StorageKey = keyof StorageDataMap;

const SessionStorageKeys: StorageKey[] = ["INIT_URL"];

export type SetStorageInput<K extends StorageKey = StorageKey> = {
  key: K;
  data: StorageDataMap[K];
};

export const setStorage = async <K extends StorageKey>(
  input: SetStorageInput<K>
) => {
  // if the key is in the SessionStorageDataMap then use sessionStorage, otherwise use localStorage
  (await SessionStorageKeys.includes(input.key))
    ? sessionStorage.setItem(input.key, JSON.stringify(input.data))
    : localStorage.setItem(input.key, JSON.stringify(input.data));
};

export const setStorageSync = <K extends StorageKey>(
  input: SetStorageInput<K>
) => {
  SessionStorageKeys.includes(input.key)
    ? sessionStorage.setItem(input.key, JSON.stringify(input.data))
    : localStorage.setItem(input.key, JSON.stringify(input.data));
};

export const setStorageSyncIfNull = <K extends StorageKey>(
  input: SetStorageInput<K>
) => {
  const data = getStorageSync(input.key);
  if (!data) {
    setStorageSync(input);
  }
};

export const getStorage = async <K extends StorageKey>(
  key: K
): Promise<StorageDataMap[K] | null> => {
  const data = (await SessionStorageKeys.includes(key))
    ? sessionStorage.getItem(key)
    : localStorage.getItem(key);
  return data ? JSON.parse(data) : null;
};

export const getStorageSync = <K extends StorageKey>(
  key: K
): StorageDataMap[K] | null => {
  const data = SessionStorageKeys.includes(key)
    ? sessionStorage.getItem(key)
    : localStorage.getItem(key);
  return data ? JSON.parse(data) : null;
};

export const removeStorage = async (key: StorageKey) => {
  if (SessionStorageKeys.includes(key)) {
    await sessionStorage.removeItem(key);
    return;
  }
  await localStorage.removeItem(key);
};

export const removeStorageSync = (key: StorageKey) => {
  if (SessionStorageKeys.includes(key)) {
    sessionStorage.removeItem(key);
    return;
  }
  localStorage.removeItem(key);
};

export const clearStorage = async () => {
  // await sessionStorage.clear();
  await localStorage.clear();
};
